import { getLicenseDetailsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import { isForegroundFetch } from "@/lib/foreground-fetch";
import { useQuery } from "@tanstack/react-query";

/**
 * Fast preview from runtime cache, then a second request that recomputes persisted totals.
 * UI should merge: refreshed data replaces preview when it arrives.
 */
export function useLicenseDetailsWithRuntimeRefresh(options: {
  licenseId: string | null | undefined;
  enabled: boolean;
}) {
  const { licenseId, enabled } = options;
  const id = licenseId ?? "";
  const baseEnabled = Boolean(enabled && licenseId);

  const previewQuery = useQuery({
    ...getLicenseDetailsOptions({
      path: { license_id: id },
      query: { refresh_runtime_cache: false },
    }),
    enabled: baseEnabled,
    staleTime: 30_000,
  });

  const refreshQuery = useQuery({
    ...getLicenseDetailsOptions({
      path: { license_id: id },
      query: { refresh_runtime_cache: true },
    }),
    enabled: baseEnabled && !!previewQuery.data,
    staleTime: 30_000,
  });

  const licenseData = refreshQuery.data ?? previewQuery.data;
  const isPreviewLoading = previewQuery.isLoading;
  // Only the recompute that follows opening the details counts; later cache
  // invalidations refresh totals already on screen and stay silent.
  const isRuntimeRefreshing =
    isForegroundFetch(refreshQuery) && previewQuery.isSuccess;

  return {
    licenseData,
    isPreviewLoading,
    isRuntimeRefreshing,
    previewError: previewQuery.error,
    refreshError: refreshQuery.error,
  };
}
